import { useEffect, useMemo, useRef, useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { computeSpineLayout, type Position } from '@/services/graph-layout';
import { deriveRespondentKey } from '@/lib/respondent-key';

// Shared layout for every value-graph surface (full canvas + journey mini
// graph): runs the elk spine layout and returns computed positions by node id.
// Recomputes only when graph *structure* changes (node ids/types + edge
// endpoints), not on every render, so dragging/styling don't re-trigger elk.
// elk is async, so results land in state; callers apply positions to their own
// node arrays (and keep the stored position for any id not in the map).
export function useSpineLayout(nodes: Node[], edges: Edge[]): Map<string, Position> {
  const [positions, setPositions] = useState<Map<string, Position>>(new Map());

  const input = useMemo(
    () => ({
      // `group` is a pure ordering hint for the layout: it keeps one
      // respondent's disjoint clusters adjacent so the column reads as one
      // block per respondent. Synthesised nodes carry no respondent and gather
      // at the end. Keyed through deriveRespondentKey rather than
      // respondentEmail alone — anonymous shared-link submissions carry no
      // email, so they would all share one absent key and interleave.
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type ?? '',
        group: deriveRespondentKey(
          (n.data as { metadata?: Record<string, unknown> } | undefined)?.metadata ?? {},
        ),
      })),
      edges: edges.map((e) => ({ source: e.source, target: e.target })),
    }),
    [nodes, edges],
  );

  const signature = useMemo(
    () =>
      JSON.stringify([
        input.nodes.map((n) => `${n.id}:${n.type}:${n.group ?? ''}`).sort(),
        input.edges.map((e) => `${e.source}->${e.target}`).sort(),
      ]),
    [input],
  );

  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    let cancelled = false;
    computeSpineLayout(inputRef.current.nodes, inputRef.current.edges).then((pos) => {
      if (!cancelled) setPositions(pos);
    });
    return () => {
      cancelled = true;
    };
  }, [signature]);

  return positions;
}
